import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const BuranGhati = () => {
  const tourData = {
    name: "Buran Ghati Trek",
    duration: "06 Nights 07 Days",
    price: "₹ 13,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935197/a5d4b1bdzzkd9nuns9zt.jpg",
    altitude: "15,000 ft",
    difficulty: "Moderate",
    region: "Himachal Pradesh",
    highlights: ["High altitude", "Mountain pass", "Alpine scenery"],
    bestTime: "June to September",
    description: "The Buran Ghati Trek is a beautiful moderate trek in Himachal Pradesh that takes you through stunning alpine scenery and crosses the challenging Buran Pass at 15,000 feet. This trek offers breathtaking views and is perfect for those looking for a high-altitude adventure."
  };

  return (
    <>
      <Helmet>
        <title>Buran Ghati Trek - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Buran Ghati Trek in Himachal Pradesh. High altitude trek with mountain pass and alpine scenery." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              High altitude trek with mountain pass and alpine scenery
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek takes you through the beautiful Buran Ghati region, offering 
                    stunning alpine scenery and challenging terrain. The journey includes 
                    crossing the Buran Pass at 15,000 feet, providing breathtaking views 
                    of the surrounding mountains and valleys. It's a perfect choice for 
                    those seeking a high-altitude adventure in the Himalayas.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>High altitude adventure</li>
                    <li>Mountain pass crossing</li>
                    <li>Alpine scenery</li>
                    <li>Moderate difficulty level</li>
                    <li>Chandranahan Lake visit</li>
                    <li>Beautiful meadows</li>
                    <li>Photography opportunities</li>
                    <li>Mountain camping experience</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: From Shimla to Janglik</h4>
                    <ul>
                      <li>Drive from Shimla to Janglik (150 km, 7-8 hours).</li>
                      <li>Arrive at Janglik, check into your guesthouse, and have lunch.</li>
                      <li>Attend a trek briefing session, explore the village, and enjoy dinner.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Janglik to Dayara Thach</h4>
                    <ul>
                      <li>Trek from Janglik to Dayara Thach (9 km, 5-6 hours).</li>
                      <li>Reach Dayara Thach, have lunch, and relax.</li>
                      <li>Explore the beautiful meadow and enjoy dinner at the campsite.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Dayara Thach to Litham</h4>
                    <ul>
                      <li>Begin trekking to Litham (6 km, 4-5 hours).</li>
                      <li>Arrive at Litham, enjoy lunch, and rest.</li>
                      <li>Visit Chandranahan Lake (optional) and return for dinner.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Acclimatization Day at Litham</h4>
                    <ul>
                      <li>Explore nearby areas or trek to Chandranahan Lake.</li>
                      <li>Return to the camp for lunch.</li>
                      <li>Relax and enjoy dinner.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: Litham to Dhunda</h4>
                    <ul>
                      <li>Trek to Dhunda (5 km, 4-5 hours).</li>
                      <li>Arrive at Dhunda, have lunch, and relax.</li>
                      <li>Prepare for the Buran Pass crossing and enjoy dinner.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 6: Dhunda to River Camp via Buran Pass</h4>
                    <ul>
                      <li>Begin the trek to Buran Pass (15,000 ft) and descend to River Camp (10 km, 8-9 hours).</li>
                      <li>Enjoy panoramic views from the pass and take photographs.</li>
                      <li>Descend carefully and reach River Camp for lunch and rest.</li>
                      <li>Enjoy a well-deserved dinner and rest at the campsite.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 7: River Camp to Barua and Drive to Shimla</h4>
                    <ul>
                      <li>Trek to Barua village (5 km, 3-4 hours) and drive back to Shimla (6-7 hours).</li>
                      <li>Arrive in Shimla and conclude your Buran Ghati Trek adventure.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>5 nights camping and 1 night at homestay</li>
                    <li>All meals during the stay (nutritious vegetarian food)</li>
                    <li>All necessary permits and entry fees</li>
                    <li>Experienced Trek Leaders and support staff</li>
                    <li>First-aid kit, regular health checks during the trek</li>
                    <li>Tents, sleeping bags, and trekking equipment</li>
                    <li>Shimla to Shimla (implying transportation from and to Shimla)</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Travel to and from Shimla</li>
                    <li>Insurance (with a recommendation for all trekkers to have insurance)</li>
                    <li>Personal gear (shoes, clothing, etc.)</li>
                    <li>Offloading of personal luggage</li>
                    <li>Additional meals or services not mentioned in the itinerary</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>15,000 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>40 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>June to September</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Shimla)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 13,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 14,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 14,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Shimla:</strong> ISBT Shimla between 06:00 AM to 07:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>High altitude adventure</li>
                    <li>Mountain pass crossing</li>
                    <li>Alpine scenery</li>
                    <li>Moderate difficulty level</li>
                    <li>Chandranahan Lake visit</li>
                    <li>Beautiful meadows</li>
                    <li>Photography opportunities</li>
                    <li>Mountain camping experience</li>
                    <li>Expert guide and support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default BuranGhati;
